import { Cart, Type, VariantGroup, VariantGroupType, VariantOption } from "../actions/types";
import { ProductTag } from "../actions/enums";
import { RestrictedZipCode, AllowedCellPhone } from "../tenant/tenantConfig";

export const getSizeNameFromTypes = (types: Type[]): string => {
  const sizeType = types.find((type) =>
    type.name.toLowerCase().includes(ProductTag.TALLE),
  );
  return sizeType ? sizeType.name : "";
};

export const getLiquidacionItemsCount = (cart: Cart[]): number => {
  return cart.reduce(
    (acc, item) =>
      acc +
      (item.options.product.types?.some(
        (type) => type.name === ProductTag.LIQUIDACION,
      )
        ? item.qty
        : 0),
    0,
  );
};

/**
 * Formatea un precio para mostrar en la UI.
 * Si el valor es entero, se omiten los decimales (1000.00 → "1000").
 * Si tiene centavos reales, se mantienen dos decimales (1000.50 → "1000.50").
 */
export const formatPrice = (
  price: string | number | null | undefined,
): string => {
  const value = Number(price);
  if (price === null || price === undefined || !Number.isFinite(value))
    return "";
  return Number.isInteger(value) ? String(value) : value.toFixed(2);
};

export const validateEmail = (email: string): boolean => {
  return /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/.test(email);
};

/**
 * Verifica si una ciudad/código postal está restringida para un tenant.
 * Las listas restrictedZipCodes y allowedCellPhones vienen del TenantConfig.
 */
export const checkRestrictedCity = (
  city: string,
  postalCode: string,
  cellPhone: string,
  restrictedZipCodes: RestrictedZipCode[] = [],
  allowedCellPhones: AllowedCellPhone[] = [],
): string => {
  if (
    restrictedZipCodes.some(
      (item) =>
        item.zipCode === postalCode ||
        item.city.toLocaleLowerCase() === city.toLocaleLowerCase(),
    ) &&
    !allowedCellPhones.some((item) => item.cellPhone === cellPhone)
  ) {
    return "No podemos concretar la compra porque en esta ciudad tenemos un revendedor exclusivo";
  }
  return "";
};

export const calculateOrderTotals = (
  subTotalCart: number,
  shippingCost: number,
  increase1: number,
  discount1: number,
) => {
  const baseSubTotal = subTotalCart + shippingCost;
  const increment = Math.round((baseSubTotal * increase1) / 100);
  const discountPayWay = Math.round((baseSubTotal * discount1) / 100);
  const total = Math.round(baseSubTotal + increment - discountPayWay);

  return {
    incrementAmount: increment,
    discountAmount: discountPayWay,
    totalCart: total,
  };
};

// ─── Sistema de variantes genéricas ──────────────────────────────────────────

/**
 * Mapa de prefijos de nombre → tipo e label de grupo.
 * Para extender con nuevos tipos: agregar entradas a este mapa.
 * El prefijo debe ir en minúsculas. El label es el que se muestra en la UI.
 */
const VARIANT_PREFIX_MAP: { prefix: string; groupType: VariantGroupType; label: string }[] = [
  { prefix: "talle", groupType: "size",  label: "Talle" },
  { prefix: "color", groupType: "color", label: "Color" },
];

/**
 * Infiere los grupos de variantes a partir del array de types del producto.
 * 
 * Reglas de detección (por prefijo, case-insensitive):
 *   "Talle S", "Talle M"  → grupo "size",  label visible "S", "M"
 *   "Color Negro"          → grupo "color", label visible "Negro"
 *   Otros                  → grupo "generic", label visible = nombre completo
 * 
 * @param typesSizes - Array de Type proveniente de la API (los variantes del producto)
 * @returns Array de VariantGroup, uno por cada grupo detectado.
 */
export const inferVariantGroups = (typesSizes: Type[]): VariantGroup[] => {
  if (!typesSizes || typesSizes.length === 0) return [];

  const groupMap = new Map<string, VariantGroup>();

  for (const type of typesSizes) {
    const nameLower = type.name.toLowerCase().trim();
    const matched = VARIANT_PREFIX_MAP.find((m) => nameLower.startsWith(m.prefix));

    let groupType: VariantGroupType;
    let groupLabel: string;
    let optionLabel: string;

    if (matched) {
      groupType = matched.groupType;
      groupLabel = matched.label;
      // Etiqueta visible: lo que queda después del prefijo y el espacio
      optionLabel = type.name.substring(matched.prefix.length).trim();
      if (!optionLabel) optionLabel = type.name; // fallback si no hay sufijo
    } else {
      // Grupo genérico para variantes no mapeadas (futuro)
      groupType = "generic";
      groupLabel = "Opción";
      optionLabel = type.name;
    }

    const option: VariantOption = {
      id: type.pivot.product_id,
      label: optionLabel,
      disabled: (type as any).stock === 0,
    };

    if (!groupMap.has(groupType)) {
      groupMap.set(groupType, { groupType, label: groupLabel, options: [] });
    }
    groupMap.get(groupType)!.options.push(option);
  }

  return Array.from(groupMap.values());
};
